import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton,
  List, ListItemButton, ListItemIcon, ListItemText,
  Avatar, Menu, MenuItem, Divider, Tooltip, Chip,
  useTheme, useMediaQuery,
} from '@mui/material'
import {
  Menu as MenuIcon, Dashboard, Business, Receipt,
  AdminPanelSettings, ChevronLeft, Logout,
  People, KeyboardArrowDown,
  CurrencyExchange, SystemUpdate,
} from '@mui/icons-material'
import { useAdminAuthStore } from '../store/adminAuthStore'
import { analytics } from '../lib/mixpanel'
import { logoutPlatformAdmin } from '../lib/adminApi'

const DRAWER_WIDTH = 272

const NAV = [
  { label: 'Overview', path: '/admin/dashboard', icon: <Dashboard />, description: 'Platform health and performance' },
  { label: 'Tenants', path: '/admin/tenants', icon: <Business />, description: 'Organizations and access' },
  { label: 'Billing & Plans', path: '/admin/billing', icon: <Receipt />, description: 'Revenue and subscriptions' },
  { label: 'Platform Admins', path: '/admin/admins', icon: <People />, description: 'Console access and owners' },
  { label: 'Exchange Rates', path: '/admin/exchange-rates', icon: <CurrencyExchange />, description: 'Currency conversion rules' },
  { label: 'App Updates', path: '/admin/app-updates', icon: <SystemUpdate />, description: 'Mobile releases and maintenance' },
]

export const adminGridSx = {
  bgcolor: 'transparent',
  border: 'none',
  color: 'text.primary',
  '--DataGrid-rowBorderColor': '#f1f5f9',
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: '#F7F9FC',
    color: '#52667A',
    borderBottom: '1px solid #E3EAF2',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  '& .MuiDataGrid-cell': {
    borderColor: '#f1f5f9',
    color: '#243B53',
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-row': {
    transition: 'background 0.12s',
    '&:hover': { bgcolor: '#F1F8FC !important' },
  },
  '& .MuiDataGrid-footerContainer': {
    bgcolor: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    color: '#64748b',
  },
  '& .MuiTablePagination-root': { color: '#64748b' },
  '& .MuiDataGrid-sortIcon': { color: '#94a3b8' },
  '& .MuiDataGrid-menuIconButton': { color: '#94a3b8' },
}

export function AdminLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { name, email } = useAdminAuthStore()

  // Track page views on route change
  useEffect(() => {
    const pageName = location.pathname.replace(/^\/admin\/?/, '') || 'dashboard'
    analytics.pageView(pageName, location.pathname)
  }, [location.pathname])

  const [drawerOpen, setDrawerOpen] = useState(!isMobile)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const currentNav = NAV.find((n) => location.pathname === n.path)
  const currentPage = currentNav?.label ?? 'Admin'

  useEffect(() => {
    setDrawerOpen(!isMobile)
  }, [isMobile])

  const handleLogout = async () => {
    await logoutPlatformAdmin()
    navigate('/admin/login', { replace: true })
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      {/* Brand */}
      <Toolbar sx={{ px: 2.5, minHeight: '64px !important', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 36, height: 36, borderRadius: 1.5 }} />
          <Box>
            <Typography variant="body2" fontWeight={850} color="text.primary" lineHeight={1.2} letterSpacing="-0.02em">
              OmniTrace
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1, fontSize: 10 }}>
              Admin Console
            </Typography>
          </Box>
        </Box>
        {!isMobile && (
          <IconButton size="small" onClick={() => setDrawerOpen(false)}
            sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}
      </Toolbar>

      {/* Role badge */}
      <Box sx={{ px: 2, py: 1.75 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.625,
          borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100',
        }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34A8E0' }} />
          <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 800, fontSize: 10.5, letterSpacing: '0.06em' }}>
            PLATFORM ADMIN
          </Typography>
        </Box>
      </Box>

      {/* Nav label */}
      <Box sx={{ px: 2.5, pb: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 600, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Menu
        </Typography>
      </Box>

      {/* Nav items */}
      <List sx={{ flex: 1, px: 1.5, pt: 0.5 }}>
        {NAV.map((item) => {
          const active = location.pathname === item.path
          return (
            <ListItemButton
              key={item.path}
              onClick={() => { navigate(item.path); if (isMobile) setDrawerOpen(false) }}
              sx={{
                borderRadius: 2, mb: 0.65, px: 1.25, py: 1,
                position: 'relative',
                color: active ? 'primary.dark' : '#52667A',
                bgcolor: active ? '#EAF7FD !important' : 'transparent',
                border: active ? '1px solid #CDECF9' : '1px solid transparent',
                fontWeight: active ? 600 : 400,
                transition: 'all 0.12s',
                '&:hover': { bgcolor: '#F7F9FC !important', color: '#243B53' },
                '& .MuiListItemIcon-root': { color: active ? 'primary.main' : '#829AB1' },
                ...(active && {
                  '&::before': {
                    content: '""', position: 'absolute', left: 0, top: '18%', bottom: '18%',
                    width: 3, borderRadius: '0 2px 2px 0', bgcolor: 'primary.main',
                  },
                }),
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, '& svg': { fontSize: 19 } }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 750 : 550 }}
                secondaryTypographyProps={{ fontSize: 10.5, color: active ? '#5A8AA3' : '#9AAABD', mt: 0.15, noWrap: true }}
              />
            </ListItemButton>
          )
        })}
      </List>

      {/* User footer */}
      <Divider sx={{ borderColor: '#f1f5f9' }} />
      <Box sx={{ p: 1.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 1.5,
          bgcolor: '#F7F9FC', border: '1px solid #EAF0F5',
        }}>
          <Avatar sx={{
            width: 32, height: 32, fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)',
          }}>
            {name?.[0]?.toUpperCase() ?? 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="#0f172a" fontWeight={600} display="block" noWrap>
              {name ?? 'Admin'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10 }} display="block" noWrap>
              {email}
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton size="small" onClick={handleLogout}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
              <Logout sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" elevation={0} sx={{
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: 'rgba(255,255,255,0.92)',
        color: 'text.primary',
        borderBottom: '1px solid #E3EAF2',
        backdropFilter: 'blur(14px)',
      }}>
        <Toolbar sx={{ gap: 1, minHeight: '70px !important', px: { xs: 1.5, sm: 2.5 } }}>
          <IconButton edge="start" onClick={() => setDrawerOpen((p) => !p)}
            sx={{ color: '#94a3b8', '&:hover': { color: '#475569' } }}>
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={750} color="text.primary" noWrap>{currentPage}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 10.5 }}>
              {currentNav?.description ?? 'Platform administration'}
            </Typography>
          </Box>

          <Chip
            label="SaaS Owner"
            size="small"
            sx={{
              bgcolor: '#EBF6FD', color: '#34A8E0',
              border: '1px solid #B3DCF2',
              fontSize: 11, fontWeight: 700, mx: 0.5, display: { xs: 'none', md: 'inline-flex' },
            }}
          />

          <Box
            role="button"
            tabIndex={0}
            aria-label="Open admin account menu"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAnchorEl(e.currentTarget) }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.625,
              borderRadius: 2.5, cursor: 'pointer',
              border: '1px solid #E3EAF2', bgcolor: '#F7F9FC',
              '&:hover': { bgcolor: '#EEF4F8', borderColor: '#C8D6E3' }, transition: 'all 0.12s',
            }}
          >
            <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 700, background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)' }}>
              {name?.[0]?.toUpperCase() ?? 'A'}
            </Avatar>
            <Typography variant="caption" color="#1e293b" fontWeight={500} sx={{ display: { xs: 'none', sm: 'block' } }}>
              {name ?? 'Admin'}
            </Typography>
            <KeyboardArrowDown sx={{ fontSize: 15, color: '#94a3b8' }} />
          </Box>

          <Menu
            anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', minWidth: 200, mt: 1 },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={600} color="#0f172a">{name}</Typography>
              <Typography variant="caption" color="#64748b">{email}</Typography>
            </Box>
            <Divider sx={{ borderColor: '#f1f5f9' }} />
            <MenuItem onClick={handleLogout}
              sx={{ color: '#ef4444', gap: 1.5, mt: 0.5, mx: 1, borderRadius: 1, '&:hover': { bgcolor: '#fef2f2' } }}>
              <Logout fontSize="small" />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH, boxSizing: 'border-box',
            bgcolor: 'background.paper', borderRight: '1px solid #E3EAF2',
            boxShadow: '8px 0 30px rgba(16,42,67,0.045)',
            transition: theme.transitions.create('width'),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{
        flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 2.5, lg: 3.5 }, mt: '70px',
        transition: theme.transitions.create('margin'),
      }}>
        <Box sx={{ width: '100%', maxWidth: 1560, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
